/**
 * Validación y armado de contratos desde la planilla de carga masiva.
 * Columnas (fila 1 = encabezado): ver COLUMNAS_CONTRATOS.
 */
import { aFecha } from "./fechas";
import { aNumero } from "./facturacion";

export const COLUMNAS_CONTRATOS = [
  "codigo",
  "cliente",
  "local",
  "fechaInicio",
  "fechaFin",
  "dia_uf",
  "superficieTotal",
  "estadoContrato",
  "facturacion",
  "renovacion",
  "meses_renovacion",
  "centroCostoContrato",
  "nombreResponsable",
  "telefonoResponsable",
] as const;

export const EJEMPLO_CONTRATO = [
  1, "76111111-6", "L101", "01-01-2026", "31-12-2026", 5, 50.5,
  "Vigente", "Mensual", "Manual", "", "004", "Juan Pérez", "+56912345678",
];

const ESTADOS = ["Vigente", "Expirado", "Cancelado", "Borrador"];
const FACTURACION = ["Mensual", "Trimestral", "Anual"];
const RENOVACION = ["Automática", "Manual", "No Renovable"];

interface ClienteRef {
  id?: string;
  codigo: string;
  nombre: string;
  rut: string;
  giro: string;
  telefono: string;
  direccion: string;
  correo: string;
  rubro: string;
  nombre_comercial: string;
}

interface LocalRef {
  id?: string;
  codigo: string;
  centroCosto: string;
  unidadLocativa: string;
  superficie: number;
  nivel: string;
  fachada: string;
  vista_planta: string;
  tipo_local: string;
  estadoLocal: string;
}

export interface ContratoExcel {
  codigo: number;
  clienteId: string;
  localId: string;
  clienteSnapshot: Omit<ClienteRef, "id">;
  localSnapshot: Omit<LocalRef, "id">;
  fechaInicio: Date;
  fechaFin: Date;
  dia_uf: number;
  superficieTotal: number;
  estadoContrato: string;
  facturacion: string;
  renovacion: string;
  meses_renovacion: number;
  centroCostoContrato: string;
  nombreResponsable: string;
  telefonoResponsable: string;
  pdfContratoUrl: string;
}

const normalizar = (v: unknown) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toUpperCase();

const texto = (v: unknown) => String(v ?? "").trim();

/** Devuelve la opción con su escritura oficial ("vigente" → "Vigente"), o null. */
export function normalizarOpcion(valor: unknown, opciones: string[]): string | null {
  const buscado = normalizar(valor);
  return opciones.find((o) => normalizar(o) === buscado) ?? null;
}

/**
 * Fecha de una celda: Excel entrega las fechas como Date a medianoche UTC,
 * que en Chile cae el día anterior; se toma el día UTC como fecha local.
 */
export function fechaExcel(valor: unknown): Date | null {
  if (valor instanceof Date) {
    if (isNaN(valor.getTime())) return null;
    return new Date(valor.getUTCFullYear(), valor.getUTCMonth(), valor.getUTCDate());
  }
  return aFecha(valor);
}

const limpiarRut = (rut: string) => rut.replace(/[.\s]/g, "").toUpperCase();

/**
 * Arma un contrato desde los valores de una fila (en el orden de COLUMNAS_CONTRATOS).
 * El cliente se busca por id, código o RUT; el local por id o código.
 */
export function construirContrato(
  fila: unknown[],
  datos: {
    clientes: ClienteRef[];
    locales: LocalRef[];
    codigosUsados: Set<number>;
  }
): { contrato: ContratoExcel } | { errores: string[] } {
  const v = (nombre: typeof COLUMNAS_CONTRATOS[number]) =>
    fila[COLUMNAS_CONTRATOS.indexOf(nombre)];
  const errores: string[] = [];

  const codigo = aNumero(v("codigo"));
  if (!Number.isInteger(codigo) || codigo <= 0) errores.push("código de contrato inválido");
  else if (datos.codigosUsados.has(codigo)) errores.push(`el código ${codigo} ya existe`);

  const claveCliente = texto(v("cliente"));
  const cliente = datos.clientes.find(
    (c) =>
      c.id === claveCliente ||
      texto(c.codigo) === claveCliente ||
      limpiarRut(texto(c.rut)) === limpiarRut(claveCliente)
  );
  if (!cliente) errores.push(`cliente "${claveCliente}" no existe (usa su RUT o código)`);

  const claveLocal = texto(v("local"));
  const local = datos.locales.find(
    (l) => l.id === claveLocal || normalizar(l.codigo) === normalizar(claveLocal)
  );
  if (!local) errores.push(`local "${claveLocal}" no existe (usa su código)`);

  const fechaInicio = fechaExcel(v("fechaInicio"));
  const fechaFin = fechaExcel(v("fechaFin"));
  if (!fechaInicio) errores.push("fecha de inicio inválida (usa DD-MM-AAAA)");
  if (!fechaFin) errores.push("fecha de término inválida (usa DD-MM-AAAA)");
  if (fechaInicio && fechaFin && fechaFin < fechaInicio)
    errores.push("la fecha de término es anterior al inicio");

  const estado = normalizarOpcion(v("estadoContrato") || "Vigente", ESTADOS);
  if (!estado) errores.push(`estado inválido (${ESTADOS.join(", ")})`);
  const facturacion = normalizarOpcion(v("facturacion") || "Mensual", FACTURACION);
  if (!facturacion) errores.push(`facturación inválida (${FACTURACION.join(", ")})`);
  const renovacion = normalizarOpcion(v("renovacion") || "Manual", RENOVACION);
  if (!renovacion) errores.push(`renovación inválida (${RENOVACION.join(", ")})`);

  const diaUf = aNumero(v("dia_uf"));
  if (diaUf < 0 || diaUf > 31) errores.push("día UF debe estar entre 1 y 31 (o vacío)");

  const superficie = aNumero(v("superficieTotal")) || local?.superficie || 0;
  if (!(superficie > 0)) errores.push("superficie inválida");

  if (errores.length || !cliente || !local || !fechaInicio || !fechaFin) return { errores };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id: _idCliente, ...clienteSnapshot } = cliente;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id: _idLocal, ...localSnapshot } = local;

  return {
    contrato: {
      codigo,
      clienteId: cliente.id ?? "",
      localId: local.id ?? "",
      clienteSnapshot: { ...clienteSnapshot, rubro: cliente.rubro || "", nombre_comercial: cliente.nombre_comercial || "" },
      localSnapshot,
      fechaInicio,
      fechaFin,
      dia_uf: diaUf,
      superficieTotal: superficie,
      estadoContrato: estado as string,
      facturacion: facturacion as string,
      renovacion: renovacion as string,
      meses_renovacion: aNumero(v("meses_renovacion")),
      centroCostoContrato: texto(v("centroCostoContrato")) || local.centroCosto || "",
      nombreResponsable: texto(v("nombreResponsable")),
      telefonoResponsable: texto(v("telefonoResponsable")),
      pdfContratoUrl: "",
    },
  };
}
